/**
 * Log Notifiche Inviate — pagina super_admin.
 * Mostra gli ultimi 100 record da /api/admin/notifications/logs con filtri
 * per canale (push/email), esito (sent/failed/skipped) ed evento.
 *
 * Auto-purge dopo 30 giorni via TTL index Mongo (vedi server.py).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Mail, Bell, CheckCircle2, XCircle, MinusCircle,
  ArrowLeft, AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const EVENT_LABELS = {
  urgent_ticket: 'Ticket Urgente',
  new_ticket: 'Nuovo Ticket',
  ticket_assigned: 'Ticket Assegnato',
  ticket_update: 'Cambio Stato',
  warning: 'Cartellino',
  warning_threshold: 'Sotto Osservazione',
  warning_revoked: 'Cartellino Annullato',
  contract_alert: 'Alert Contratto',
  sla_breach: 'Violazione SLA',
  test_email: 'Test Email',
  generic: 'Generico',
};

const StatusBadge = ({ status }) => {
  if (status === 'sent') return <Badge className="bg-green-100 text-green-700 gap-1 font-normal"><CheckCircle2 className="w-3 h-3" />Inviata</Badge>;
  if (status === 'failed') return <Badge className="bg-red-100 text-red-700 gap-1 font-normal"><XCircle className="w-3 h-3" />Fallita</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 gap-1 font-normal"><MinusCircle className="w-3 h-3" />Saltata</Badge>;
};

const ChannelBadge = ({ channel }) => (
  channel === 'push'
    ? <Badge className="bg-indigo-100 text-indigo-700 gap-1 font-normal"><Bell className="w-3 h-3" />Push</Badge>
    : <Badge className="bg-blue-100 text-blue-700 gap-1 font-normal"><Mail className="w-3 h-3" />Email</Badge>
);

const fmtDT = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const NotificationLogsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ sent: 0, failed: 0, skipped: 0 });
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState('all');
  const [status, setStatus] = useState('all');
  const [eventType, setEventType] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (channel !== 'all') params.append('channel', channel);
      if (status !== 'all') params.append('status', status);
      if (eventType !== 'all') params.append('event_type', eventType);
      const { data } = await api.get(`/admin/notifications/logs?${params.toString()}`);
      setItems(data.items || []);
      setStats(data.stats || { sent: 0, failed: 0, skipped: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento log');
    } finally {
      setLoading(false);
    }
  }, [channel, status, eventType]);

  useEffect(() => { load(); }, [load]);

  // Set unico di event_type presenti (per il filtro dinamico)
  const eventTypesSeen = useMemo(() => {
    const s = new Set(items.map(i => i.event_type).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6">
        <Card><CardContent className="p-6 text-center text-slate-500">Accesso riservato ai Super Admin.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="notification-logs-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/strumenti-dati" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Strumenti Dati
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Log Notifiche Inviate</h1>
          <p className="text-slate-500 mt-1 text-sm">Ultime 100 notifiche (push + email). Retention 30 giorni.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-logs">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Aggiorna
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-green-200/70">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <div><p className="text-[11px] text-slate-500 uppercase tracking-wide">Inviate</p><p className="text-xl font-bold text-slate-900">{stats.sent}</p></div>
          </CardContent>
        </Card>
        <Card className="border-red-200/70">
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-600" />
            <div><p className="text-[11px] text-slate-500 uppercase tracking-wide">Fallite</p><p className="text-xl font-bold text-slate-900">{stats.failed}</p></div>
          </CardContent>
        </Card>
        <Card className="border-slate-200/70">
          <CardContent className="p-4 flex items-center gap-3">
            <MinusCircle className="w-5 h-5 text-slate-500" />
            <div><p className="text-[11px] text-slate-500 uppercase tracking-wide">Saltate</p><p className="text-xl font-bold text-slate-900">{stats.skipped}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Canale</label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-40" data-testid="filter-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="push">Push</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Esito</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="sent">Inviate</SelectItem>
                <SelectItem value="failed">Fallite</SelectItem>
                <SelectItem value="skipped">Saltate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-500">Evento</label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger className="w-48" data-testid="filter-event"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {eventTypesSeen.map(ev => (
                  <SelectItem key={ev} value={ev}>{EVENT_LABELS[ev] || ev}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Cronologia</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm flex flex-col items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-slate-400" />
              Nessun log corrispondente ai filtri.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Data</th>
                    <th className="px-4 py-2 text-left">Canale</th>
                    <th className="px-4 py-2 text-left">Evento</th>
                    <th className="px-4 py-2 text-left">Destinatario</th>
                    <th className="px-4 py-2 text-left">Ticket</th>
                    <th className="px-4 py-2 text-left">Esito</th>
                    <th className="px-4 py-2 text-left">Errore</th>
                  </tr>
                </thead>
                <tbody data-testid="logs-tbody">
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2 whitespace-nowrap text-slate-600">{fmtDT(it.timestamp)}</td>
                      <td className="px-4 py-2"><ChannelBadge channel={it.channel} /></td>
                      <td className="px-4 py-2 text-slate-700">{EVENT_LABELS[it.event_type] || it.event_type}</td>
                      <td className="px-4 py-2 text-slate-700 max-w-[220px] truncate" title={it.recipient_name ? `${it.recipient_name} (${it.recipient})` : it.recipient}>
                        {it.recipient_name || it.recipient || '-'}
                      </td>
                      <td className="px-4 py-2">
                        {it.ticket_id ? (
                          <Link to={`/tickets/${it.ticket_id}`} className="text-blue-600 hover:underline text-xs">{it.ticket_id.slice(-6)}</Link>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="px-4 py-2"><StatusBadge status={it.status} /></td>
                      <td className="px-4 py-2 text-xs text-red-600 max-w-[240px] truncate" title={it.error || ''}>{it.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationLogsPage;
